import { createContext } from "react";
import type {
  AsyncTaskStatuses,
  IntakeAnswers,
  IntakeAnswerValue,
  Product,
  SetupProgress,
  TaskId,
} from "../registry/types";
import type {
  TaskCompletionEvent,
  TaskCompletionListener,
} from "./use-task-completion-events";
import type { WidgetStateApi } from "./use-widget-state";

export interface SetupGuideContextValue {
  /** Active product the widget is showing. Null when no in-flight setup. */
  activeProduct: Product | null;

  /** Progress row for the active product (null = not yet loaded or none). */
  activeProgress: SetupProgress | null;

  /** All in-flight progress rows for the team, keyed by product. */
  progressByProduct: Partial<Record<Product, SetupProgress>>;

  /** True while the initial server fetch hasn't completed. */
  loading: boolean;

  /** Product-provided async task statuses for the active product. */
  asyncTaskStatuses: AsyncTaskStatuses;

  /** True when the intake modal needs to render (active product, not yet completed). */
  showIntake: boolean;

  /**
   * Draft intake answers. Lifted out of the IntakeModal so they survive
   * the modal unmounting + remounting when the merchant navigates away
   * from product routes (the host route-scopes the modal). Initialized
   * from `activeProgress.intakeAnswers` and reset when the active row
   * changes (different product or fresh row).
   */
  intakeAnswers: IntakeAnswers;

  /** Update a single intake answer in the draft. */
  setIntakeAnswer: (questionId: string, value: IntakeAnswerValue) => void;

  /** Widget visibility + close/collapse mutators (per-device + team-dismiss aware). */
  widget: WidgetStateApi;

  // Mutators ------------------------------------------------------------

  /** Submits intake answers + advances to the widget view. */
  submitIntake: (product: Product, answers: IntakeAnswers) => Promise<void>;

  /** Marks the whole guide complete/incomplete for the active team. */
  setGuideCompletion: (product: Product, completed: boolean) => Promise<void>;

  /** Creates or revives a setup row so intake can open from an explicit CTA. */
  startGuide: (
    product: Product,
    entrySource: string,
    options?: { forceResetIntake?: boolean },
  ) => Promise<void>;

  /** Fire-and-forget visit recording. Never throws. */
  recordTaskVisited: (product: Product, taskId: TaskId) => void;

  /** Fire-and-forget intake step recording for reporting mirrors. Never throws. */
  recordIntakeStep: (product: Product, setupStepId: string) => void;

  /** Manual completion for tasks without a derivable predicate. */
  markTaskComplete: (product: Product, taskId: TaskId) => Promise<void>;

  /** Per-task dismiss (optional tasks the merchant doesn't want to do). */
  dismissTask: (product: Product, taskId: TaskId) => Promise<void>;

  /** Team-scoped dismiss — hides widget + header tab for everyone on the team. */
  dismissGuideForTeam: (product: Product) => Promise<void>;

  /**
   * Team-scoped temporary deferral — sets `deferredUntil` so the intake
   * modal stays suppressed until that timestamp passes. Distinct from
   * `dismissGuideForTeam`, which is permanent. The widget reminder keeps
   * showing during the deferral window. If `untilDate` is omitted the
   * server defaults to 7 days from now.
   */
  deferGuideForTeam: (product: Product, untilDate?: Date) => Promise<void>;

  // Event channel -------------------------------------------------------

  /**
   * Contextual modals call this after their mutation succeeds. Triggers
   * a progress refetch so derived completion flips immediately.
   */
  signalTaskCompletion: (event: TaskCompletionEvent) => void;

  /**
   * Subscribe to task completion attempts (used by component tests +
   * future cross-cutting consumers). Returns unsubscribe.
   */
  subscribeTaskCompletion: (listener: TaskCompletionListener) => () => void;
}

export const SetupGuideContext = createContext<SetupGuideContextValue | null>(
  null,
);
